import React, { useState, useEffect } from "react";
import { axiosPrivate } from "../../../api/axios";

const DesktopConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Editable values
  const [betaEndDate, setBetaEndDate] = useState("");
  const [minAppVersion, setMinAppVersion] = useState("");
  const [syncIntervalMs, setSyncIntervalMs] = useState("");
  const [telemetryEnabled, setTelemetryEnabled] = useState(false);
  const [poinikoEnabled, setPoinikoEnabled] = useState(false);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosPrivate.post("/admin/desktop/stats");
      const cfg = response.data.config;
      setConfig(cfg);

      // Initialize form values
      if (cfg) {
        setBetaEndDate(cfg.betaEndDate || "");
        setMinAppVersion(cfg.minAppVersion || "");
        setSyncIntervalMs(cfg.syncIntervalMs?.toString() || "300000");
        setTelemetryEnabled(cfg.featureFlags?.TELEMETRY_ENABLED ?? false);
        setPoinikoEnabled(cfg.featureFlags?.POINIKO_ENABLED ?? false);
        setAutoUpdateEnabled(cfg.featureFlags?.AUTO_UPDATE_ENABLED ?? false);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to fetch config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const updateConfig = async (key, value) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await axiosPrivate.post("/admin/desktop/config", { key, value });
      setSuccess(`Updated ${key} successfully`);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || `Failed to update ${key}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBetaEndDate = () => updateConfig("beta_end_date", betaEndDate);
  const handleSaveMinAppVersion = () => updateConfig("min_app_version", minAppVersion);
  const handleSaveSyncInterval = () => updateConfig("sync_interval_ms", syncIntervalMs);

  const handleToggleTelemetry = () => {
    const newValue = !telemetryEnabled;
    setTelemetryEnabled(newValue);
    updateConfig("feature_telemetry_enabled", newValue.toString());
  };

  const handleTogglePoiniko = () => {
    const newValue = !poinikoEnabled;
    setPoinikoEnabled(newValue);
    updateConfig("feature_poiniko_enabled", newValue.toString());
  };

  const handleToggleAutoUpdate = () => {
    const newValue = !autoUpdateEnabled;
    setAutoUpdateEnabled(newValue);
    updateConfig("feature_auto_update_enabled", newValue.toString());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Beta End Date */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Beta End Date</h3>
        <div className="flex gap-4">
          <input
            type="date"
            value={betaEndDate}
            onChange={(e) => setBetaEndDate(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSaveBetaEndDate}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Desktop apps will show beta expiry notice based on this date
        </p>
      </div>

      {/* Min App Version */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Minimum App Version</h3>
        <div className="flex gap-4">
          <input
            type="text"
            value={minAppVersion}
            onChange={(e) => setMinAppVersion(e.target.value)}
            placeholder="1.0.0"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSaveMinAppVersion}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Apps below this version will be prompted to update
        </p>
      </div>

      {/* Sync Interval */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Sync Interval (milliseconds)</h3>
        <div className="flex gap-4">
          <input
            type="number"
            value={syncIntervalMs}
            onChange={(e) => setSyncIntervalMs(e.target.value)}
            min="60000"
            step="60000"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSaveSyncInterval}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Default: 300000 (5 minutes). Minimum: 60000 (1 minute)
        </p>
      </div>

      {/* Feature Flags */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Feature Flags</h3>
        <div className="space-y-4">
          {/* Telemetry */}
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <div className="font-medium">Telemetry</div>
              <div className="text-sm text-gray-500">
                Collect usage analytics from desktop apps
              </div>
            </div>
            <button
              onClick={handleToggleTelemetry}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                telemetryEnabled ? "bg-blue-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  telemetryEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Poiniko */}
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <div className="font-medium">Poiniko (Criminal Cases)</div>
              <div className="text-sm text-gray-500">
                Enable criminal case tracking feature
              </div>
            </div>
            <button
              onClick={handleTogglePoiniko}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                poinikoEnabled ? "bg-blue-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  poinikoEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Auto Update */}
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="font-medium">Auto Update</div>
              <div className="text-sm text-gray-500">
                Automatically check and prompt for app updates
              </div>
            </div>
            <button
              onClick={handleToggleAutoUpdate}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoUpdateEnabled ? "bg-blue-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoUpdateEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopConfig;
